import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Container, Spinner } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * /subscribe/success — polls the backend until the webhook completes
 * account creation. Displays a friendly wait state while Stripe fires
 * the checkout.session.completed webhook (usually < 5 seconds).
 */
export default function SubscribeSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id') || '';
  const [status, setStatus] = useState('waiting');   // waiting | ready | failed
  const [checkout, setCheckout] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus('failed'); return undefined; }
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/api/subscribe/status/${sessionId}`);
        const pc = res.data?.data;
        if (cancelled) return;
        setCheckout(pc);
        if (pc?.status === 'completed') { setStatus('ready'); return; }
        if (pc?.status === 'failed') { setStatus('failed'); return; }
      } catch (e) { /* keep polling */ }
      tries += 1;
      if (tries > 30) { setStatus('failed'); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <Container className="py-5 text-center" style={{ maxWidth: 620 }} data-testid="subscribe-success-page">
      <Card className="shadow-sm">
        <Card.Body className="p-4">
          {status === 'waiting' && (
            <>
              <Spinner animation="border" variant="primary" className="mb-3" />
              <h3>Finalizing your subscription…</h3>
              <p className="text-muted">Your payment succeeded. We&apos;re creating your account now — this usually takes just a few seconds.</p>
            </>
          )}
          {status === 'ready' && (
            <>
              <div className="fs-1 mb-2">🎉</div>
              <h3>You&apos;re all set!</h3>
              <p className="text-muted mb-3">
                Your Campaign Manager account has been created. Please log in with the email and password you provided.
              </p>
              {checkout?.candidate_id ? (
                <Alert variant="success" className="text-start">
                  Your account is linked to your selected candidate profile. Head to the dashboard to start managing.
                </Alert>
              ) : (
                <Alert variant="info" className="text-start">
                  Your subscription is active. Our team will review your candidate assignment request and reach out shortly.
                </Alert>
              )}
              <Button as={Link} to="/login" variant="primary" size="lg" data-testid="success-login-btn">
                Go to login
              </Button>
            </>
          )}
          {status === 'failed' && (
            <>
              <div className="fs-1 mb-2">⚠️</div>
              <h3>Something took longer than expected</h3>
              <p className="text-muted">If your card was charged, your account will be created within a few minutes. If you don&apos;t receive access, please contact support.</p>
              <Button as={Link} to="/" variant="outline-secondary">Back to home</Button>
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}
